import { useSesamyAuthTokenContext } from "@/contexts/sesamy-auth-token-context";
import type { FetchOptions as NativeFetchOptions } from "@/utils/native-fetch";
import * as nativeFetch from "@/utils/native-fetch";

type FetchOptions = {
  url: string;
  body?: any;
  headers?: Record<string, string>;
};

type FetchMethod = (options: NativeFetchOptions) => Promise<any>;

export default function useSesamyAuthFetch() {
  const { accessToken, getNewSesamyToken } = useSesamyAuthTokenContext();

  const withTokenRefresh = async (
    fetchMethod: FetchMethod,
    options: FetchOptions,
    isRetry = false,
  ): Promise<any> => {
    if (!accessToken) {
      throw new Error("Access token is not available");
    }

    try {
      return await fetchMethod({ ...options, accessToken });
    } catch (error) {
      const isTokenExpired =
        error instanceof Error &&
        error.message.includes("TOKEN EXPIRED") &&
        !isRetry;

      if (isTokenExpired) {
        await getNewSesamyToken();
        return await withTokenRefresh(fetchMethod, options, true);
      } else {
        console.error(
          error instanceof Error
            ? error.message
            : "An error occurred during fetch",
        );
      }

      throw error;
    }
  };

  const fetcher = (url: string) => withTokenRefresh(nativeFetch.get, { url });
  const isReady = !!accessToken;

  const get = (options: FetchOptions) =>
    withTokenRefresh(nativeFetch.get, options);
  const post = (options: FetchOptions) =>
    withTokenRefresh(nativeFetch.post, options);
  const del = (options: FetchOptions) =>
    withTokenRefresh(nativeFetch.del, options);
  const put = (options: FetchOptions) =>
    withTokenRefresh(nativeFetch.put, options);
  const patch = (options: FetchOptions) =>
    withTokenRefresh(nativeFetch.patch, options);

  return { fetcher, isReady, get, post, del, put, patch };
}
